import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { getAlertsByUser, deleteAlertById } from '../../features/alerts/alerts'
import { showSnackBar } from '../Snackbar/Snackbar'
import AlertItem from './AlertItem'
import AddAlertModal from './AddAlertModal'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import { StorageGetItem, USER } from '../../lib/storage'
import { useMyContextLoading } from '../LoadingContextProvider/LoadingContextProvider'
import { LuTrash2 } from "react-icons/lu"

const AccountAlerts = ({ userId, externalRefreshTrigger = 0 }) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const [alerts, setAlerts] = useState([])
  const [editingAlert, setEditingAlert] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [userRole, setUserRole] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [alertToDelete, setAlertToDelete] = useState(null)

  // Get user role
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role || '')
    })
  }, [])

  // Check if user can manage alerts (admin or internal user)
  const canManageAlerts = userRole === 'customer' || userRole === 'user'

  const fetchAlerts = async () => {
    try {
      setIsLoading(true)
      const response = await dispatch(getAlertsByUser({ userId }))
      if (response.payload?.data) {
        setAlerts(response.payload.data)
      }
    } catch (error) {
      console.error('Error fetching alerts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      fetchAlerts()
    }
  }, [userId, refreshTrigger, externalRefreshTrigger])

  const openDeleteConfirm = (alertId) => {
    setAlertToDelete(alertId)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!alertToDelete) return
    triggerLoading(true)
    try {
      const response = await dispatch(deleteAlertById(alertToDelete))
      if (response.payload?.data) {
        showSnackBar('success', 'Alert deleted successfully')
        setRefreshTrigger(prev => prev + 1)
      } else {
        showSnackBar('error', response.payload || 'Failed to delete alert')
      }
    } catch (error) {
      showSnackBar('error', 'Failed to delete alert')
    } finally {
      triggerLoading(false)
      setDeleteConfirmOpen(false)
      setAlertToDelete(null)
    }
  }

  const handleEdit = (alert) => {
    setEditingAlert(alert)
  }

  const handleAlertSaved = () => {
    setRefreshTrigger(prev => prev + 1)
    setEditingAlert(null)
  }

  const handleModalClose = () => {
    setEditingAlert(null)
  }

  // Don't render anything if no alerts and user can't manage
  if (!isLoading && alerts.length === 0 && !canManageAlerts) {
    return null
  }

  return (
    <div className="mb-4">
      {/* Alerts List */}
      {alerts.length > 0 && (
        <div className="flex flex-col gap-1">
          {alerts.map((alert) => (
            <AlertItem
              key={alert._id}
              alert={alert}
              canManage={canManageAlerts}
              onEdit={() => handleEdit(alert)}
              onDelete={() => openDeleteConfirm(alert._id)}
            />
          ))}
        </div>
      )}

      {/* Add Alert Modal - button is hidden, moved to ContactProfile 3-dots menu */}
      {canManageAlerts && (
        <AddAlertModal
          userId={userId}
          editingAlert={editingAlert}
          onClose={handleModalClose}
          onSaved={handleAlertSaved}
          hideButton={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
        title="Are you sure you want to delete this alert?"
        modalButtonTitle="Delete"
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        functionButton={handleConfirmDelete}
      >
        This action cannot be undone.
      </ConfirmModal>
    </div>
  )
}

AccountAlerts.propTypes = {
  userId: PropTypes.string.isRequired,
  externalRefreshTrigger: PropTypes.number,
}

export default AccountAlerts
